import {
  IWorkout,
  IWorkoutDay,
  IBlock,
  IStepNode,
  IStepIntensity,
  IStepRecovery,
} from '../../../models/programming';
import {
  BLOCK_TYPE,
  BLOCK_TYPE_COLORS,
  BLOCK_TYPE_LABELS,
  LOAD_UNIT,
  EXERCISE_METHOD,
  WORKOUT_TYPE,
  WORKOUT_CATEGORY,
  isEnduranceInputMode,
} from '../../../enums/programming';

// --- Local form types ---

export interface FormLoad {
  value: string;
  unit: LOAD_UNIT;
}

export interface SetDetail {
  setNumber: number;
  reps: string;
  rest: string;
  /** Carico per questa serie (usato da metodi a serie variabile: pyramid, dropset) */
  loadValue?: string;
  /** Unità del carico per-serie */
  loadUnit?: LOAD_UNIT;
}

export interface EmomSubExercise {
  exerciseId: string;
  exerciseName: string;
  reps: string;
}

/** Personalizzazione per-round EMOM (analogo a SetDetail per le serie classiche) */
export interface EmomRoundDetail {
  round: number;
  reps: string;
  loadValue: string;
  loadUnit: LOAD_UNIT;
  tempo: string;
  notes: string;
}

// ─── Condizionamento step-based (EPIC 9021f6): form types (string-based per gli input) ───
export interface FormStepIntensity {
  /** 'profile' = % di un riferimento dell'atleta · 'absolute' = valore diretto */
  mode: 'profile' | 'absolute';
  ref: 'vam' | 'vift' | 'ftp' | '';
  pct: string;
  absValue: string;
  absUnit: string;
}
export interface FormStepRecovery {
  enabled: boolean;
  mode: 'time' | 'distance';
  value: string;
  /** Recupero attivo: intensità del recupero (es. 50% VAM). Opzionale. */
  intensity: FormStepIntensity;
}
export interface FormStepNode {
  _localId: string;
  kind: 'single' | 'group';
  // single
  volumeMode: 'time' | 'distance';
  volumeValue: string;
  intensity: FormStepIntensity;
  recovery: FormStepRecovery;
  label: string;
  // group
  repeat: string;
  children: FormStepNode[];
  setRecovery: FormStepRecovery;
}

export interface FormExercise {
  _localId: string;
  exerciseId: string;
  exerciseName: string;
  /**
   * Input mode dal catalogo esercizi (strength/cardio_field/sprint/...).
   * Pilota il filtro unità di carico nel dropdown. Opzionale per backward
   * compatibility con esercizi legacy senza classificazione.
   */
  defaultInputMode?: string;
  /** Slug del catalogo: identifica i mezzi canonici (es. 'distance-run', 'rsa')
   *  per i preset-scorciatoia dello step builder (BUG B). */
  slug?: string;
  searchQuery: string;
  sets: string;
  reps: string;
  loads: FormLoad[];
  rest: string;
  method: EXERCISE_METHOD | '';
  methodConfig: Record<string, string>;
  notes: string;
  supersetGroup: string; // '' = no superset, 'A' | 'B' | 'C' etc.
  emomSubExercises: EmomSubExercise[];
  emomRoundDetails: EmomRoundDetail[]; // empty = usa valori globali, filled = per-round
  isUnilateral: boolean;
  setsDetail: SetDetail[]; // empty = use global reps/rest, filled = per-set
  steps: FormStepNode[]; // mezzi di condizionamento (method='steps'), vuoto altrimenti
}

export interface FormBlock {
  _localId: string;
  title: string;
  type: BLOCK_TYPE;
  exercises: FormExercise[];
}

export interface FormDay {
  _localId: string;
  dayNumber: number;
  title: string;
  blocks: FormBlock[];
}

// --- Helpers ---

let _localIdCounter = 0;
export const newLocalId = () => `local_${++_localIdCounter}`;

export const emptyLoad = (): FormLoad => ({ value: '', unit: LOAD_UNIT.KG });

export const emptyExercise = (): FormExercise => ({
  _localId: newLocalId(),
  exerciseId: '',
  exerciseName: '',
  defaultInputMode: undefined,
  slug: undefined,
  searchQuery: '',
  sets: '3',
  reps: '8',
  loads: [emptyLoad()],
  rest: '90"',
  method: '',
  methodConfig: {},
  notes: '',
  supersetGroup: '',
  emomSubExercises: [],
  emomRoundDetails: [],
  isUnilateral: false,
  setsDetail: [],
  steps: [],
});

// ─── Condizionamento step-based: factory + parse/serialize ───
export const emptyStepIntensity = (): FormStepIntensity => ({
  mode: 'profile',
  ref: 'vam',
  pct: '',
  absValue: '',
  absUnit: 'km/h',
});
export const emptyStepRecovery = (): FormStepRecovery => ({
  enabled: false,
  mode: 'time',
  value: '',
  intensity: emptyStepIntensity(),
});
export const emptyStepSingle = (): FormStepNode => ({
  _localId: newLocalId(),
  kind: 'single',
  volumeMode: 'distance',
  volumeValue: '',
  intensity: emptyStepIntensity(),
  recovery: emptyStepRecovery(),
  label: '',
  repeat: '',
  children: [],
  setRecovery: emptyStepRecovery(),
});
export const emptyStepGroup = (): FormStepNode => ({
  _localId: newLocalId(),
  kind: 'group',
  volumeMode: 'distance',
  volumeValue: '',
  intensity: emptyStepIntensity(),
  recovery: emptyStepRecovery(),
  label: '',
  repeat: '6',
  children: [emptyStepSingle()],
  setRecovery: emptyStepRecovery(),
});

const intensityFromModel = (i?: IStepIntensity): FormStepIntensity => ({
  mode: i?.absValue != null ? 'absolute' : 'profile',
  ref: i?.ref ?? 'vam',
  pct: i?.pct != null ? String(i.pct) : '',
  absValue: i?.absValue != null ? String(i.absValue) : '',
  absUnit: i?.absUnit ?? 'km/h',
});
const recoveryFromModel = (r?: IStepRecovery): FormStepRecovery => ({
  enabled: !!r,
  mode: r?.mode ?? 'time',
  value: r?.value != null ? String(r.value) : '',
  intensity: intensityFromModel(r?.intensity),
});
export const stepFromModel = (n: IStepNode): FormStepNode => ({
  _localId: newLocalId(),
  kind: n.kind === 'group' ? 'group' : 'single',
  volumeMode: n.volumeMode === 'time' ? 'time' : 'distance',
  volumeValue: n.volumeValue != null ? String(n.volumeValue) : '',
  intensity: intensityFromModel(n.intensity),
  recovery: recoveryFromModel(n.recovery),
  label: n.label ?? '',
  repeat: n.repeat != null ? String(n.repeat) : '',
  children: Array.isArray(n.children) ? n.children.map(stepFromModel) : [],
  setRecovery: recoveryFromModel(n.setRecovery),
});

const intensityToModel = (i: FormStepIntensity): IStepIntensity | undefined => {
  if (i.mode === 'absolute') {
    if (i.absValue === '') return undefined;
    return { absValue: parseFloat(i.absValue), absUnit: i.absUnit || undefined };
  }
  if (i.pct === '' || !i.ref) return undefined;
  return { ref: i.ref, pct: parseFloat(i.pct) };
};
const recoveryToModel = (r: FormStepRecovery): IStepRecovery | undefined => {
  if (!r.enabled || r.value === '') return undefined;
  const intensity = intensityToModel(r.intensity);
  return {
    mode: r.mode,
    value: parseFloat(r.value),
    ...(intensity ? { intensity } : {}),
  };
};
export const stepToModel = (n: FormStepNode): IStepNode => {
  if (n.kind === 'group') {
    return {
      kind: 'group',
      repeat: n.repeat ? parseInt(n.repeat, 10) : undefined,
      children: n.children.map(stepToModel),
      setRecovery: recoveryToModel(n.setRecovery),
    };
  }
  return {
    kind: 'single',
    volumeMode: n.volumeMode,
    volumeValue: n.volumeValue ? parseFloat(n.volumeValue) : undefined,
    intensity: intensityToModel(n.intensity),
    recovery: recoveryToModel(n.recovery),
    label: n.label || undefined,
  };
};

export const emptyBlock = (type: BLOCK_TYPE = BLOCK_TYPE.MAX_STRENGTH): FormBlock => ({
  _localId: newLocalId(),
  title: BLOCK_TYPE_LABELS[type],
  type,
  exercises: [emptyExercise()],
});

export const emptyDay = (dayNumber: number): FormDay => ({
  _localId: newLocalId(),
  dayNumber,
  title: '',
  blocks: [emptyBlock(BLOCK_TYPE.WARMUP)],
});

export const blockFromModel = (block: IBlock): FormBlock => ({
  _localId: newLocalId(),
  title: block.title,
  type: block.type,
  exercises: block.exercises.map((ex): FormExercise => {
    // exerciseId può essere populated (oggetto) o stringa
    const exIdRaw = ex.exerciseId as any;
    const isPopulated = typeof exIdRaw === 'object' && exIdRaw !== null;
    const exerciseObj = isPopulated ? exIdRaw : ex.exercise;
    const id = isPopulated ? exIdRaw._id : ex.exerciseId;
    const name = exerciseObj?.name?.it || exerciseObj?.name?.en || id || '';

    const rawConfig = ex.methodConfig ?? {};
    // Extract emomSubExercises from methodConfig.exercises (array), keep the rest as string map
    const emomSubExercisesRaw = Array.isArray((rawConfig as any).exercises)
      ? (rawConfig as any).exercises
      : [];
    const emomSubExercises: EmomSubExercise[] = emomSubExercisesRaw.map((sub: any) => {
      const subIdRaw = sub.exerciseId;
      const isPopulatedSub = typeof subIdRaw === 'object' && subIdRaw !== null;
      const subId = isPopulatedSub ? subIdRaw._id : subIdRaw;
      const subName = isPopulatedSub
        ? subIdRaw.name?.it || subIdRaw.name?.en || subId || ''
        : sub.exerciseName || subId || '';
      return { exerciseId: subId, exerciseName: subName, reps: String(sub.reps ?? '') };
    });

    // Parse emomRoundDetails from methodConfig.roundDetails (array)
    const roundDetailsRaw = Array.isArray((rawConfig as any).roundDetails)
      ? (rawConfig as any).roundDetails
      : [];
    const emomRoundDetails: EmomRoundDetail[] = roundDetailsRaw.map((rd: any, i: number) => ({
      round: rd.round ?? i + 1,
      reps: String(rd.reps ?? ''),
      loadValue: String(rd.loadValue ?? ''),
      loadUnit: rd.loadUnit ?? LOAD_UNIT.KG,
      tempo: String(rd.tempo ?? ''),
      notes: String(rd.notes ?? ''),
    }));

    const configEntries = Object.entries(rawConfig)
      .filter(([k]) => k !== 'exercises' && k !== 'rounds' && k !== 'roundDetails')
      .map(([k, v]) => [k, String(v)]);

    return {
      _localId: newLocalId(),
      exerciseId: id,
      exerciseName: name,
      defaultInputMode: exerciseObj?.defaultInputMode,
      slug: (exerciseObj as any)?.slug,
      searchQuery: name,
      sets: String(ex.sets),
      reps: ex.reps,
      loads: ex.loads.length > 0
        ? ex.loads.map((l) => ({ value: String(l.value), unit: l.unit }))
        : [emptyLoad()],
      rest: ex.rest || '',
      method: ex.method || '',
      methodConfig: Object.fromEntries(configEntries),
      notes: ex.notes || '',
      supersetGroup: ex.supersetGroup || '',
      emomSubExercises,
      emomRoundDetails,
      isUnilateral: ex.isUnilateral ?? false,
      setsDetail: Array.isArray((ex as any).setsDetail)
        ? (ex as any).setsDetail.map((sd: any, i: number) => ({
            setNumber: sd.setNumber ?? i + 1,
            reps: String(sd.reps ?? ''),
            rest: sd.rest ?? '',
            loadValue: sd.loadValue !== undefined && sd.loadValue !== null ? String(sd.loadValue) : undefined,
            loadUnit: sd.loadUnit ?? undefined,
          }))
        : [],
      steps: Array.isArray((ex as any).steps)
        ? (ex as any).steps.map(stepFromModel)
        : [],
    };
  }),
});

export const dayFromModel = (day: IWorkoutDay): FormDay => ({
  _localId: newLocalId(),
  dayNumber: day.dayNumber,
  title: day.title || '',
  blocks: day.blocks.map(blockFromModel),
});

export interface OccurrenceMode {
  assignmentId: string;
  eventId: string;
  scope: 'single' | 'this_and_future' | 'all';
  workoutData: IWorkout | null; // null = fetch via getAssignmentOccurrence
}

export const buildPayload = (
  title: string,
  workoutType: WORKOUT_TYPE,
  tags: string,
  athleteNotes: string,
  days: FormDay[],
  isTemplate: boolean,
  workoutCategory?: WORKOUT_CATEGORY,
): Partial<IWorkout> => ({
  title: title.trim(),
  type: workoutType,
  // Il backend salva il campo come "category" (non "workoutCategory")
  category: workoutCategory as string | undefined,
  tags: tags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean),
  athleteNotes: athleteNotes.trim() || undefined,
  isTemplate,
  days: days.map((day, idx) => ({
    dayNumber: idx + 1,
    title: day.title.trim() || undefined,
    blocks: day.blocks.map((block) => ({
      _id: undefined as unknown as string,
      title: block.title,
      type: block.type,
      color: BLOCK_TYPE_COLORS[block.type],
      exercises: block.exercises
        .filter((ex) => ex.exerciseId)
        .map((ex, exIdx) => ({
          exerciseId: ex.exerciseId,
          order: exIdx,
          sets: parseInt(ex.sets, 10) || 1,
          reps: ex.reps,
          loads: ex.loads
            .filter((l) => l.value !== '')
            .map((l) => ({ value: parseFloat(l.value) || 0, unit: l.unit })),
          rest: ex.rest || undefined,
          method: (ex.method as EXERCISE_METHOD) || undefined,
          notes: ex.notes || undefined,
          methodConfig: (() => {
            const base = Object.keys(ex.methodConfig).length > 0
              ? Object.fromEntries(
                  Object.entries(ex.methodConfig)
                    .filter(([_, v]) => v !== '' && v !== undefined)
                    .map(([k, v]) => {
                      // Mappa tabataSets → sets per il backend
                      const key = k === 'tabataSets' ? 'sets' : k;
                      // isExplosive → boolean
                      if (key === 'isExplosive') return [key, v === 'true'];
                      return [key, isNaN(Number(v)) ? v : Number(v)];
                    })
                )
              : {};
            if (ex.method === EXERCISE_METHOD.EMOM) {
              // Normalizza durata e intervallo in base alle unità scelte
              const durationUnit = ex.methodConfig.durationUnit || 'min';
              const intervalUnit = ex.methodConfig.intervalUnit || 'sec';
              const durationVal = parseFloat(ex.methodConfig.totalDurationMin ?? '0') || 0;
              const intervalVal = parseFloat(ex.methodConfig.intervalSec ?? '0') || 0;
              const durationInSec = durationUnit === 'min' ? durationVal * 60 : durationVal;
              const intervalInSec = intervalUnit === 'min' ? intervalVal * 60 : intervalVal;
              const rounds = intervalInSec > 0 ? Math.floor(durationInSec / intervalInSec) : 0;

              if (ex.emomSubExercises.length > 0) {
                return {
                  ...base,
                  rounds,
                  exercises: ex.emomSubExercises
                    .filter((s) => s.exerciseId)
                    .map((s) => ({ exerciseId: s.exerciseId, reps: s.reps })),
                };
              }
              if (ex.emomRoundDetails.length > 0) {
                return {
                  ...base,
                  rounds,
                  roundDetails: ex.emomRoundDetails.map((rd) => ({
                    round: rd.round,
                    reps: rd.reps || undefined,
                    loadValue: rd.loadValue ? parseFloat(rd.loadValue) || rd.loadValue : undefined,
                    loadUnit: rd.loadUnit || undefined,
                    tempo: rd.tempo || undefined,
                    notes: rd.notes || undefined,
                  })),
                };
              }
              // Nessuna personalizzazione: serializza solo rounds
              if (rounds > 0) {
                return { ...base, rounds };
              }
            }
            return Object.keys(base).length > 0 ? base : undefined;
          })(),
          supersetGroup: ex.supersetGroup || undefined,
          supersetOrder: ex.supersetGroup
            ? block.exercises
                .filter((e) => e.supersetGroup === ex.supersetGroup)
                .findIndex((e) => e._localId === ex._localId)
            : undefined,
          isUnilateral: ex.isUnilateral || undefined,
          setsDetail: ex.setsDetail.length > 0
            ? ex.setsDetail.map((sd) => ({
                setNumber: sd.setNumber,
                reps: sd.reps,
                rest: sd.rest,
                ...(sd.loadValue !== undefined && sd.loadValue !== ''
                  ? { loadValue: parseFloat(sd.loadValue) || 0 }
                  : {}),
                ...(sd.loadUnit !== undefined ? { loadUnit: sd.loadUnit } : {}),
              }))
            : undefined,
          steps:
            isEnduranceInputMode(ex.defaultInputMode) && ex.steps.length > 0
              ? ex.steps.map(stepToModel)
              : undefined,
        })),
      createdBy: '' as string,
      directorId: '' as string,
      isTemplate,
      isLeopartTemplate: false,
      tags: [],
      createdAt: '',
      updatedAt: '',
    })) as IBlock[],
  })) as IWorkoutDay[],
});
